import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';
import { money } from '@/lib/format';
import { cn } from '@/lib/cn';

interface FlashEvent {
  id: string;
  pnl: number;
  ticker: string;
  at: number;
}

interface PnlFlashStore {
  flashes: FlashEvent[];
  push: (pnl: number, ticker: string) => void;
  pop: (id: string) => void;
}

export const usePnlFlash = create<PnlFlashStore>()(
  immer((set) => ({
    flashes: [],
    push: (pnl, ticker) => set((s) => {
      s.flashes.push({ id: nanoid(), pnl, ticker, at: Date.now() });
      if (s.flashes.length > 5) s.flashes.shift();
    }),
    pop: (id) => set((s) => { s.flashes = s.flashes.filter((f) => f.id !== id); }),
  })),
);

export function PnlFlash() {
  const flashes = usePnlFlash((s) => s.flashes);
  return (
    <div className="pointer-events-none fixed top-16 left-1/2 z-[80] flex -translate-x-1/2 flex-col items-center gap-2">
      {flashes.map((f) => (
        <FlashItem key={f.id} flash={f} />
      ))}
    </div>
  );
}

function FlashItem({ flash }: { flash: FlashEvent }) {
  const pop = usePnlFlash((s) => s.pop);
  const [stage, setStage] = useState(0);
  const positive = flash.pnl >= 0;

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 30);
    const t2 = setTimeout(() => setStage(2), 1800);  // begin fade
    const t3 = setTimeout(() => pop(flash.id), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [flash.id, pop]);

  return (
    <div
      className={cn(
        'rounded-md border px-4 py-2 font-mono text-mono-md font-bold tabular-nums backdrop-blur-md transition-all duration-300',
        positive
          ? 'border-[var(--positive)] bg-[var(--positive)]/10 text-[var(--positive)]'
          : 'border-[var(--negative)] bg-[var(--negative)]/10 text-[var(--negative)]',
        stage === 0 && 'translate-y-2 opacity-0 scale-90',
        stage === 1 && 'translate-y-0 opacity-100 scale-100',
        stage === 2 && '-translate-y-2 opacity-0 scale-95',
      )}
      style={{ textShadow: positive ? '0 0 12px rgba(78,201,176,0.6)' : '0 0 12px rgba(240,107,107,0.6)' }}
    >
      {positive ? '+' : ''}{money(flash.pnl)} {flash.ticker}
    </div>
  );
}
